import { BuildClass, DateTimeObj, lx } from '../../../../universal';
import { match, matchP, React } from '../../../lib';
import { HelpTooltip } from '../help';
import { AssistantTimeline, groupTimelineBlocks } from './assistant-timeline';
import { MarkdownRenderer } from './markdown-renderer';
import { CHAT_INTELLIGENCE_TIERS, getAttachmentUrl } from './types';
const CopyIcon = (props) => (React.createElement("img", { className: props.className, src: "/static/img/i8/material-outline-copy.svg" }));
const CheckIcon = (props) => (React.createElement("img", { className: props.className, src: "/static/img/i8/ios16-tick.svg" }));
const DeleteIcon = (props) => (React.createElement("img", { className: props.className, src: "/static/img/i8/color-trash-delete.svg" }));
const RerunIcon = (props) => (React.createElement("img", { className: props.className, src: "/static/img/i8/ios16-refresh.svg" }));
const MessageFooter = (props) => {
    const shouldShowCopyButton = props.showCopyButton ?? Boolean(props.textContent);
    const isCopyDisabled = props.textContent.trim().length === 0;
    if ((!props.hideTimestamp && props.timestamp) ||
        shouldShowCopyButton ||
        props.onRerun ||
        props.onDelete ||
        props.metadataSlot ||
        props.participantName) {
        return (React.createElement("div", { className: BuildClass({
                'flex flex-col gap-0.5 mt-0.5': true,
                'w-full items-end': props.position === 'centre',
                'items-start': props.position === 'left',
                'items-end': props.position === 'right',
            }) },
            React.createElement("div", { className: "flex items-center gap-2" },
                props.durationLabel && (React.createElement("span", { className: BuildClass({
                        'text-xs text-[oklch(var(--chat-l-text-muted)_0_0)]': true,
                        'opacity-0 group-hover:opacity-100 transition-opacity': true,
                    }) }, props.durationLabel)),
                shouldShowCopyButton && (React.createElement("button", { type: "button", onClick: () => {
                        props.onCopy();
                    }, disabled: isCopyDisabled, className: BuildClass({
                        'p-0.5 rounded transition-all cursor-pointer': true,
                        'opacity-0 group-hover:opacity-100': true,
                        'text-[oklch(var(--chat-l-text-faint)_0_0)]': true,
                        'hover:text-[oklch(var(--chat-l-text-muted)_0_0)]': true,
                    }), title: isCopyDisabled ? 'No text to copy' : 'Copy message' }, props.copied ? (React.createElement(CheckIcon, { className: "w-3 h-3" })) : (React.createElement(CopyIcon, { className: "w-3 h-3" })))),
                props.onRerun && (React.createElement("button", { type: "button", onClick: () => {
                        props.onRerun?.();
                    }, className: BuildClass({
                        'p-0.5 rounded transition-all cursor-pointer': true,
                        'opacity-0 group-hover:opacity-100': true,
                        'text-[oklch(var(--chat-l-text-faint)_0_0)]': true,
                        'hover:text-[oklch(var(--chat-l-text-muted)_0_0)]': true,
                    }), title: "Rerun response" },
                    React.createElement(RerunIcon, { className: "w-3 h-3" }))),
                props.onDelete && (React.createElement("button", { type: "button", onClick: () => {
                        props.onDelete?.();
                    }, className: BuildClass({
                        'p-0.5 rounded transition-all cursor-pointer': true,
                        'opacity-0 group-hover:opacity-100': true,
                        'text-[oklch(var(--chat-l-text-faint)_0_0)]': true,
                        'hover:text-[oklch(var(--chat-l-text-muted)_0_0)]': true,
                    }), title: "Delete message" },
                    React.createElement(DeleteIcon, { className: "w-3 h-3" }))),
                props.metadataSlot,
                !props.hideTimestamp && props.timestamp && (React.createElement(HelpTooltip, { title: React.createElement("div", { style: {
                            fontSize: '13px',
                            lineHeight: '1.2',
                            textAlign: 'left',
                        } }, formatTimestampWithUsageLines(props.timestamp, props.tokenUsage, props.intelligence).map((line, index) => (React.createElement("div", { key: index }, line || React.createElement(React.Fragment, null, "\u00A0"))))) },
                    React.createElement("span", { className: "text-xs text-[oklch(var(--chat-l-text-muted)_0_0)] cursor-help" },
                        props.senderID ? `User #${props.senderID} - ` : '',
                        formatTimestamp(props.timestamp))))),
            props.participantName && (React.createElement("span", { className: "text-xs font-medium text-[oklch(var(--chat-l-text-subtle)_0_0)]" }, props.participantName))));
    }
    return null;
};
export const ThreadMessage = (props) => {
    const role = props.content[0]?.role ?? 'assistant';
    const isUser = role === 'user';
    const isCurrentUser = props.groupChat
        ? props.senderID != null && props.senderID === props.currentUserID
        : isUser;
    const isOtherHuman = props.groupChat && isUser && !isCurrentUser;
    const isAssistant = role === 'assistant';
    const position = match([
        isAssistant,
        Boolean(props.groupChat),
        isCurrentUser,
    ])
        .with([true, matchP._, matchP._], () => 'centre')
        .with([false, true, true], () => 'left')
        .with([false, true, false], () => 'right')
        .otherwise(() => 'right');
    const participantName = props.groupChat && props.senderID
        ? (props.participantNames?.[props.senderID] ?? `User #${props.senderID}`)
        : null;
    const postingUserID = role === 'user' ? (props.senderID ?? props.currentUserID ?? null) : null;
    const assistantTimeline = React.useMemo(() => isAssistant
        ? groupTimelineBlocks(props.content, props.isStreaming ?? false)
        : null, [isAssistant, props.content, props.isStreaming]);
    const durationLabel = formatDurationLabel(props.timeTaken?.total);
    const textContent = React.useMemo(() => {
        if (assistantTimeline) {
            return assistantTimeline.textSegments
                .filter(segment => segment.isFinal)
                .map(segment => segment.text.text)
                .join('\n\n');
        }
        return props.content
            .filter((b) => b.type === 'text')
            .map(b => b.text)
            .join('\n\n');
    }, [assistantTimeline, props.content]);
    const [copied, setCopied] = React.useState(false);
    const handleCopy = async () => {
        if (!textContent) {
            return;
        }
        if (!navigator.clipboard) {
            return;
        }
        await navigator.clipboard.writeText(textContent);
        setCopied(true);
        setTimeout(() => {
            setCopied(false);
        }, 2000);
    };
    if (isAssistant) {
        if (!assistantTimeline) {
            return React.createElement(React.Fragment, null);
        }
        return (React.createElement("div", { className: BuildClass([
                {
                    'flex w-full group': true,
                    'justify-center': true,
                },
                props.className,
            ]) },
            React.createElement("div", { className: "flex flex-col min-w-0 gap-0.5 w-full items-center" },
                React.createElement(AssistantTimeline, { timeline: assistantTimeline, isStreaming: props.isStreaming ?? false, collapseLevel: props.collapseLevel ?? (props.isStreaming ? 1 : 0), onCollapseLevelChange: props.onCollapseLevelChange ?? (() => { }), expandedItems: props.expandedItems ?? new Set(), onToggleItem: props.onToggleItem ?? (() => { }), currentStatus: props.currentStatus, toolRenderers: props.toolRenderers, toolHandlers: props.toolHandlers, thoughtAccess: props.thoughtAccess, theme: props.theme }),
                MessageFooter({
                    textContent,
                    showCopyButton: true,
                    copied,
                    onCopy: () => {
                        handleCopy().catch(err => {
                            console.error(err);
                        });
                    },
                    onDelete: props.onDelete,
                    onRerun: props.onRerun,
                    hideTimestamp: props.hideTimestamp,
                    timestamp: props.timestamp,
                    tokenUsage: props.tokenUsage,
                    intelligence: props.intelligence,
                    senderID: postingUserID,
                    metadataSlot: props.metadataSlot,
                    participantName,
                    durationLabel,
                    position: 'centre',
                }))));
    }
    return (React.createElement("div", { className: BuildClass([
            {
                'flex w-full group': true,
                'gap-3': position === 'left',
                'gap-3 flex-row-reverse': position === 'right',
                'justify-center': position === 'centre',
            },
            props.className,
        ]) },
        position !== 'centre' && !props.hideAvatar && (React.createElement(MessageAvatar, { avatar: props.avatar, isUser: isCurrentUser, isOtherHuman: isOtherHuman })),
        React.createElement("div", { className: BuildClass({
                'flex flex-col min-w-0': true,
                'gap-1 max-w-[80%] items-start': position === 'left',
                'gap-1 max-w-[80%] items-end': position === 'right',
                'gap-0.5 w-full items-center': position === 'centre',
            }) },
            props.content.map((block, idx) => (React.createElement(ContentBlock, { key: idx, block: block, position: position, isOtherHuman: isOtherHuman, isStreaming: props.isStreaming, bubbleClassName: props.bubbleClassName }))),
            MessageFooter({
                textContent,
                copied,
                onCopy: () => {
                    handleCopy().catch(err => {
                        console.error(err);
                    });
                },
                onDelete: props.onDelete,
                onRerun: undefined,
                hideTimestamp: props.hideTimestamp,
                timestamp: props.timestamp,
                tokenUsage: props.tokenUsage,
                intelligence: props.intelligence,
                senderID: postingUserID,
                metadataSlot: props.metadataSlot,
                participantName,
                durationLabel: null,
                position,
            }))));
};
const MessageAvatar = (props) => {
    if (props.avatar?.url) {
        return (React.createElement("img", { src: props.avatar.url, alt: match([props.isUser, props.isOtherHuman ?? false])
                .with([true, matchP._], () => 'You')
                .with([false, true], () => 'Participant')
                .otherwise(() => 'Assistant'), className: "w-8 h-8 rounded-full object-cover flex-shrink-0" }));
    }
    return (React.createElement("div", { className: BuildClass({
            'w-8 h-8 rounded-full flex-shrink-0': true,
            'flex items-center justify-center': true,
            'text-xs font-medium': true,
            'bg-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_var(--chat-hue))]': Boolean(props.isUser && !props.isOtherHuman),
            'bg-[oklch(0.75_0.12_250)]': Boolean(props.isOtherHuman),
            'text-white': Boolean(props.isUser || props.isOtherHuman),
            'bg-[oklch(var(--chat-l-surface)_0_0)]': !props.isUser && !props.isOtherHuman,
            'text-[oklch(var(--chat-l-text-subtle)_0_0)]': !props.isUser && !props.isOtherHuman,
        }) }, match([props.isUser, props.isOtherHuman ?? false])
        .with([true, false], () => 'U')
        .with([false, true], () => 'P')
        .otherwise(() => 'A')));
};
const ContentBlock = (props) => {
    const { block, position, isOtherHuman } = props;
    const isBubble = position !== 'centre';
    if (block.type === 'new-step') {
        return (React.createElement("div", { className: BuildClass({
                'w-full h-px my-1': true,
                'bg-[oklch(var(--chat-l-text)_0_0_/_0.1)]': true,
            }) }));
    }
    if (block.type === 'attachment-manifest') {
        return React.createElement(React.Fragment, null);
    }
    if (block.type === 'text') {
        if (isBubble) {
            return (React.createElement(UserTextBlock, { text: block.text, bubbleClassName: props.bubbleClassName, isOtherHuman: isOtherHuman }));
        }
        return (React.createElement(AssistantTextBlock, { text: block.text, isStreaming: props.isStreaming, bubbleClassName: props.bubbleClassName }));
    }
    if (block.type === 'error') {
        return (React.createElement("div", { className: BuildClass({
                'rounded-lg px-3 py-2 text-sm': true,
                'bg-[oklch(0.95_0.03_25)]': true,
                'border border-[oklch(0.85_0.08_25)]': true,
                'text-[oklch(0.45_0.15_25)]': true,
            }) },
            React.createElement("span", { className: "mr-1.5" }, "\u26A0\uFE0F"),
            block.error));
    }
    if (block.type === 'source') {
        return (React.createElement("a", { href: block.source.url, target: "_blank", rel: "noopener noreferrer", className: BuildClass({
                'inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs': true,
                'bg-[oklch(var(--chat-l-surface)_0_0_/_0.5)]': true,
                'text-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_var(--chat-hue))]': true,
                'hover:underline transition-colors': true,
            }) },
            React.createElement("span", { className: "text-[oklch(var(--chat-l-text-muted)_0_0)]" }, "\uD83D\uDD17"),
            React.createElement("span", { className: "truncate max-w-[200px]" }, block.source.title ?? block.source.url)));
    }
    if (block.type === 'image-pending') {
        return (React.createElement("div", { className: BuildClass({
                'rounded-lg overflow-hidden': true,
                'border border-[oklch(var(--chat-l-text)_0_0_/_0.1)]': true,
            }) },
            React.createElement("img", { src: `data:image/webp;base64,${block.image.data}`, alt: block.image.filename, className: "max-w-full max-h-64 object-contain" }),
            React.createElement("div", { className: BuildClass({
                    'px-2 py-1 text-xs truncate': true,
                    'bg-[oklch(var(--chat-l-surface)_0_0)]': true,
                    'text-[oklch(var(--chat-l-text-muted)_0_0)]': true,
                }) }, block.image.filename)));
    }
    if (block.type === 'image') {
        const storageUrl = getAttachmentUrl(block.image.guid);
        return (React.createElement("div", { onClick: () => {
                window.open(storageUrl, '_blank', 'noopener,noreferrer');
            }, className: BuildClass({
                'rounded-lg overflow-hidden cursor-pointer': true,
                'border border-[oklch(var(--chat-l-text)_0_0_/_0.1)]': true,
                'transition-shadow hover:shadow-sm': true,
                'hover:border-[oklch(var(--chat-l-text)_0_0_/_0.2)]': true,
            }) },
            React.createElement("img", { src: storageUrl, alt: block.image.filename, className: "max-w-full max-h-64 object-contain" }),
            React.createElement("div", { className: BuildClass({
                    'px-2 py-1 text-xs truncate': true,
                    'bg-[oklch(var(--chat-l-surface)_0_0)]': true,
                    'text-[oklch(var(--chat-l-text-muted)_0_0)]': true,
                }) }, block.image.filename)));
    }
    if (block.type === 'file-pending' || block.type === 'file') {
        const isPDF = block.file.mimeType.includes('pdf');
        const isXLSX = block.file.mimeType.includes('spreadsheet') ||
            block.file.mimeType.includes('excel');
        const attachmentUrl = block.type === 'file' ? getAttachmentUrl(block.file.guid) : null;
        return (React.createElement("div", { onClick: attachmentUrl
                ? () => window.open(attachmentUrl, '_blank', 'noopener,noreferrer')
                : undefined, className: BuildClass({
                'flex items-center gap-2 px-3 py-2 rounded-lg': true,
                'bg-[oklch(var(--chat-l-surface)_0_0)]': true,
                'border border-[oklch(var(--chat-l-text)_0_0_/_0.1)]': true,
                'cursor-pointer transition-colors': Boolean(attachmentUrl),
                'hover:bg-[oklch(var(--chat-l-surface)_0_0_/_0.85)]': Boolean(attachmentUrl),
                'hover:border-[oklch(var(--chat-l-text)_0_0_/_0.2)]': Boolean(attachmentUrl),
            }) },
            React.createElement("div", { className: BuildClass({
                    'w-10 h-10 rounded flex items-center justify-center': true,
                    'bg-[oklch(0.95_0.05_25)] text-[oklch(0.55_0.2_25)]': isPDF,
                    'bg-[oklch(0.95_0.05_145)] text-[oklch(0.45_0.2_145)]': isXLSX,
                    'bg-[oklch(var(--chat-l-surface)_0_0)] text-[oklch(var(--chat-l-text-muted)_0_0)]': !isPDF && !isXLSX,
                }) },
                isPDF && (React.createElement("svg", { className: "w-5 h-5", fill: "currentColor", viewBox: "0 0 24 24" },
                    React.createElement("path", { d: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4z" }))),
                isXLSX && (React.createElement("svg", { className: "w-5 h-5", fill: "currentColor", viewBox: "0 0 24 24" },
                    React.createElement("path", { d: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM8 17h8v-2H8v2zm0-4h8v-2H8v2z" })))),
            React.createElement("div", { className: "flex-1 min-w-0" },
                React.createElement("div", { className: BuildClass({
                        'text-sm font-medium truncate': true,
                        'text-[oklch(var(--chat-l-text)_0_0)]': true,
                    }) }, block.file.filename),
                React.createElement("div", { className: BuildClass({
                        'text-xs': true,
                        'text-[oklch(var(--chat-l-text-muted)_0_0)]': true,
                    }) }, formatFileSize(block.file.size)))));
    }
    if (block.type === 'reasoning' ||
        block.type === 'search' ||
        block.type === 'presentation' ||
        block.type === 'change' ||
        block.type === 'event') {
        console.warn('Ungrouped thinking block:', block);
        return React.createElement(React.Fragment, null);
    }
    return React.createElement(React.Fragment, null);
};
const UserTextBlock = (props) => (React.createElement("div", { className: BuildClass([
        {
            'rounded-xl px-4 py-3': true,
            'bg-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_var(--chat-hue))]': !props.isOtherHuman,
            'bg-[oklch(0.75_0.12_250)]': Boolean(props.isOtherHuman),
            'text-[oklch(1_0_0)]': true,
        },
        props.bubbleClassName ?? null,
    ]) },
    React.createElement("div", { className: "text-base leading-snug whitespace-pre-wrap select-text" }, props.text)));
const AssistantTextBlock = (props) => (React.createElement("div", { className: BuildClass([
        {
            'w-full': true,
            'rounded-lg px-4 py-3': true,
            'bg-[oklch(var(--chat-l-surface)_0_0)]': true,
            'border border-[oklch(var(--chat-l-text)_0_0_/_0.2)]': true,
        },
        props.bubbleClassName,
    ]) },
    React.createElement("div", { className: BuildClass({
            'text-left select-text': true,
            'text-[oklch(var(--chat-l-text)_0_0)]': true,
        }) }, props.text.trim().length === 0 ? (React.createElement("div", { className: "text-sm italic text-[oklch(var(--chat-l-text-muted)_0_0)]" }, "Completed without text response")) : (React.createElement(MarkdownRenderer, { content: props.text, largeText: true })))));
const parseMessageTimestamp = (timestamp) => {
    if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(timestamp)) {
        const parsedIso = DateTimeObj.parseLuxon(lx.DateTime.fromISO(timestamp));
        return parsedIso.lx.isValid ? parsedIso : null;
    }
    const parsed = DateTimeObj.parse(timestamp, undefined, true);
    if (parsed?.lx.isValid) {
        return parsed;
    }
    const parsedIso = DateTimeObj.parseLuxon(lx.DateTime.fromISO(timestamp));
    return parsedIso.lx.isValid ? parsedIso : null;
};
const formatTimestamp = (timestamp) => {
    const parsed = parseMessageTimestamp(timestamp);
    return parsed ? parsed.fmt('HH:mm') : '';
};
const formatTimestampFull = (timestamp) => {
    const parsed = parseMessageTimestamp(timestamp);
    return parsed ? parsed.fmt('ccc, d LLL yyyy HH:mm:ss') : '';
};
const ROUGH_EXCHANGE_RATE_USD_TO_AUD = 1.5;
const formatTimestampWithUsage = (timestamp, usage, intelligence) => {
    const datePart = formatTimestampFull(timestamp);
    const intelligenceName = CHAT_INTELLIGENCE_TIERS.find(tier => tier.key === intelligence)?.name;
    if (!usage && !intelligenceName) {
        return datePart;
    }
    const fmt = (n) => n.toLocaleString();
    const lines = [
        datePart,
        '',
        intelligenceName ? `Reasoning: ${intelligenceName}` : null,
        intelligenceName && usage ? '' : null,
        ...(usage
            ? [
                `Input: ${fmt(usage.inputTokens)} tokens (${fmt(usage.cachedInputTokens ?? 0)} cached)`,
                `Output: ${fmt(usage.outputTokens)} tokens (${fmt(usage.reasoningTokens ?? 0)} reasoning)`,
                `Total: ${fmt(usage.totalTokens)} tokens`,
                `Cost: ${(usage.costCents * ROUGH_EXCHANGE_RATE_USD_TO_AUD).toFixed(1)} cents (AUD)`,
            ]
            : []),
    ].filter(Boolean);
    return lines.join('\n');
};
const formatTimestampWithUsageLines = (timestamp, usage, intelligence) => formatTimestampWithUsage(timestamp, usage, intelligence).split('\n');
const formatDurationLabel = (seconds) => {
    if (seconds == null) {
        return null;
    }
    const rounded = Math.max(0, Math.round(seconds));
    if (rounded < 60) {
        return `${rounded}s`;
    }
    const minutes = Math.floor(rounded / 60);
    const remainingSeconds = rounded % 60;
    return remainingSeconds === 0 ? `${minutes}m` : `${minutes}m ${remainingSeconds}s`;
};
const formatFileSize = (bytes) => {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
//# sourceMappingURL=thread-message.js.map